import { apiService } from './api';

export interface ClientTeamUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  last_login_at?: string | null;
  created_at?: string;
}

export interface SampleTrackingData {
  id: string;
  sample_id: string;
  external_id?: string;
  nom_projet?: string;
  site_minier?: string;
  type_echantillon: string;
  statut: string;
  priorite: string;
  date_reception: Date;
  date_fin_preparation?: Date;
  date_fin_analyse?: Date;
  date_pret?: Date;
  duree_workflow_jours: number;
  progress_percentage: number;
  current_location: string;
  selected_laboratory_methods: string[];
  estimated_completion?: Date;
  certificate_generated: boolean;
  certificate_number?: string;
  metadonnees?: any; // Add metadata for batch information and other sample data
}

export interface ClientAnalytics {
  total_samples: number;
  samples_this_month: number;
  samples_completed: number;
  samples_in_progress: number;
  samples_pending: number;
  average_turnaround_days: number;
  fastest_turnaround_days: number;
  slowest_turnaround_days: number;
  on_time_delivery_percentage: number;
  monthly_trends: Array<{
    month: string;
    samples_submitted: number;
    samples_completed: number;
    avg_turnaround: number;
  }>;
  sample_type_distribution: Array<{
    type: string;
    count: number;
    percentage: number;
  }>;
  status_distribution: Array<{
    status: string;
    count: number;
    percentage: number;
    color: string;
  }>;
  laboratory_distribution: Array<{
    laboratory: string;
    count: number;
    avg_turnaround: number;
  }>;
  priority_analysis: Array<{
    priority: string;
    count: number;
    avg_turnaround: number;
  }>;
}

export interface ClientDashboardOverview {
  client_info: {
    id: string;
    code: string;
    company_name: string;
    contact_person?: string;
    email?: string;
    phone?: string;
  };
  analytics: ClientAnalytics;
  recent_samples: SampleTrackingData[];
  urgent_samples: SampleTrackingData[];
  ready_certificates: Array<{
    id: string;
    sample_id: string;
    certificate_number: string;
    generated_at: Date;
    download_url: string;
  }>;
}

export interface SampleFilters {
  search?: string;
  status?: string;
  type?: string;
  project?: string;
  date_from?: string;
  date_to?: string;
}

export interface SamplesResponse {
  samples: SampleTrackingData[];
  total: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface SampleTrackingDetails {
  sample: SampleTrackingData;
  timeline: Array<{
    id: string;
    from_status?: string;
    to_status: string;
    date: Date;
    user?: {
      name: string;
      role: string;
    };
    reason?: string;
    duration_minutes?: number;
  }>;
  tracking_history: Array<{
    id: string;
    action_type: string;
    description: string;
    date: Date;
    user?: {
      name: string;
      role: string;
    };
    location?: string;
    notes?: string;
  }>;
  analysis_results: Array<{
    id: string;
    method: string;
    status: string;
    analysis_date: Date;
    validation_date?: Date;
    analyst?: {
      name: string;
      role: string;
    };
    results_summary: any;
  }>;
  estimated_timeline: any;
}

export interface CertificatesResponse {
  certificates: Array<{
    id: string;
    certificate_number: string;
    sample_id: string;
    sample_external_id?: string;
    project_name?: string;
    generated_at: Date;
    certificate_type: string;
    status: string;
    download_url: string;
  }>;
  total: number;
  page: number;
  totalPages: number;
}

export interface CertificateDownload {
  download_url: string;
  expires_at: Date;
  certificate_number: string;
  sample_id: string;
}

export interface ProjectData {
  project_name: string;
  total_samples: number;
  completed_samples: number;
  active_samples: number;
  completion_rate: number;
  avg_turnaround_days?: number;
}

export interface WorkflowStatus {
  active_samples: number;
  status_breakdown: Array<{
    status: string;
    laboratory?: string;
    count: number;
  }>;
  last_updated: Date;
}

export interface NotificationData {
  id: string;
  type: string;
  title: string;
  message: string;
  sample_id?: string;
  date: Date;
  read: boolean;
  priority: string;
}

export interface NotificationsResponse {
  notifications: NotificationData[];
  unread_count: number;
}

class ClientDashboardService {
  private baseUrl = '/client-dashboard';

  /**
   * Get complete dashboard overview with analytics and recent activity
   */
  async getDashboardOverview(): Promise<ClientDashboardOverview> {
    // For admin users, let the backend auto-select the client
    const result = await apiService.get<ClientDashboardOverview>(`${this.baseUrl}/overview`);
    
    return result;
  }

  /**
   * Get client samples with filtering and pagination
   */
  async getClientSamples(
    page: number = 1,
    limit: number = 20,
    filters: SampleFilters = {}
  ): Promise<SamplesResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add filters to params
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    // For admin users, we might need to specify a client_id
    // Check if we have a stored client preference or get the first available client
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (currentUser.role === 'ADMIN' || currentUser.role === 'LABORATORY_MANAGER') {
      // For now, let the backend handle client selection automatically
      // In the future, we could add client selection UI
    }

    return apiService.get<SamplesResponse>(`${this.baseUrl}/samples?${params.toString()}`);
  }

  /**
   * Get detailed tracking information for a specific sample
   */
  async getSampleTracking(sampleId: string): Promise<SampleTrackingDetails> {
    return apiService.get<SampleTrackingDetails>(`${this.baseUrl}/sample/${sampleId}/tracking`);
  }

  /**
   * Get detailed analytics for a specific period
   */
  async getDetailedAnalytics(period: string = '90d'): Promise<ClientAnalytics> {
    return apiService.get<ClientAnalytics>(`${this.baseUrl}/analytics/detailed?period=${period}`);
  }

  /**
   * Get all available certificates with pagination
   */
  async getClientCertificates(
    page: number = 1,
    limit: number = 20,
    search?: string
  ): Promise<CertificatesResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      params.append('search', search);
    }

    return apiService.get<CertificatesResponse>(`${this.baseUrl}/certificates?${params.toString()}`);
  }

  /**
   * Download a specific certificate
   */
  async downloadCertificate(certificateId: string): Promise<CertificateDownload> {
    try {
      const response = await apiService.get<CertificateDownload>(`${this.baseUrl}/certificate/${certificateId}/download`);
      return response;
    } catch (error: any) {
      throw new Error(`Failed to download certificate: ${error.message}`);
    }
  }

  /**
   * View certificate content for real-time viewing
   */
  async viewCertificate(certificateId: string): Promise<{
    html_content: string;
    certificate_number: string;
    sample_id: string;
    generated_at: Date;
  }> {
    try {
      // Use the certificates endpoint for viewing
      const response = await fetch(`/api/v1/certificates/${certificateId}/download?format=html`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to view certificate');
      }

      const html_content = await response.text();
      
      // Get certificate metadata
      const certData = await apiService.get<{ success: boolean; data: any }>(`/certificates/${certificateId}`);
      
      return {
        html_content,
        certificate_number: certData.data.certificate_number,
        sample_id: certData.data.sample.sample_id,
        generated_at: certData.data.generated_at,
      };
    } catch (error: any) {
      throw new Error(`Failed to view certificate: ${error.message}`);
    }
  }

  /**
   * Download all certificates as Excel (simplified)
   */
  async downloadAllCertificatesExcel(filters?: any): Promise<Blob> {
    try {
      // For now, we'll export the certificates list as CSV since Excel endpoint doesn't exist
      const certificates = await this.getClientCertificates(1, 1000); // Get all certificates
      
      // Convert to CSV format
      const csvHeader = 'Certificate Number,Sample ID,Client Reference,Project,Generated Date,Status\n';
      const csvContent = certificates.certificates.map(cert => 
        `"${cert.certificate_number}","${cert.sample_id}","${cert.sample_external_id || 'N/A'}","${cert.project_name || 'N/A'}","${cert.generated_at}","${cert.status}"`
      ).join('\n');
      
      const csvBlob = new Blob([csvHeader + csvContent], { type: 'text/csv;charset=utf-8;' });
      return csvBlob;
    } catch (error: any) {
      throw new Error(`Failed to export certificates: ${error.message}`);
    }
  }

  /**
   * Download certificate as HTML (direct link)
   */
  async downloadCertificateHTML(certificateId: string): Promise<void> {
    try {
      // The certificate download endpoint requires a JWT, so a plain
      // window.open(download_url) (no Authorization header) fails. Fetch the
      // document with the bearer token and open it from an in-memory blob.
      const response = await fetch(`/api/v1/certificates/${certificateId}/download?format=html`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to download certificate');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        // Popup blocked — fall back to a direct download.
        const a = document.createElement('a');
        a.href = url;
        a.download = `certificate-${certificateId}.html`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error: any) {
      throw new Error(`Failed to download certificate: ${error.message}`);
    }
  }

  /**
   * Client team (sub-accounts) management. Lets a client create and manage
   * several login users that all access the same client account.
   */
  async getClientTeam(): Promise<{ users: ClientTeamUser[] }> {
    return apiService.get<{ users: ClientTeamUser[] }>(`${this.baseUrl}/team`);
  }

  async createClientTeamUser(payload: {
    email: string;
    password: string;
    firstName: string;
    lastName?: string;
  }): Promise<ClientTeamUser> {
    return apiService.post<ClientTeamUser>(`${this.baseUrl}/team`, payload);
  }

  async deactivateClientTeamUser(userId: string): Promise<{ success: boolean }> {
    return apiService.delete<{ success: boolean }>(`${this.baseUrl}/team/${userId}`);
  }

  /**
   * Get all client projects with sample counts
   */
  async getClientProjects(): Promise<ProjectData[]> {
    return apiService.get<ProjectData[]>(`${this.baseUrl}/projects`);
  }

  /**
   * Get real-time workflow status for all active samples
   */
  async getWorkflowStatus(): Promise<WorkflowStatus> {
    return apiService.get<WorkflowStatus>(`${this.baseUrl}/workflow-status`);
  }

  async getWorkflowCounts(): Promise<{ stageCounts: Record<string, number>; validationCounts: Record<string, number> }> {
    return apiService.get(`${this.baseUrl}/workflow-counts`);
  }

  /**
   * Get client notifications and updates
   */
  async getClientNotifications(unreadOnly: boolean = false): Promise<NotificationsResponse> {
    const params = unreadOnly ? '?unread_only=true' : '';
    return apiService.get<NotificationsResponse>(`${this.baseUrl}/notifications${params}`);
  }

  /**
   * Real-time data refresh utilities
   */
  async refreshOverview(): Promise<ClientDashboardOverview> {
    return this.getDashboardOverview();
  }

  async refreshSamples(currentFilters: SampleFilters = {}, page: number = 1): Promise<SamplesResponse> {
    return this.getClientSamples(page, 20, currentFilters);
  }

  async refreshCertificates(): Promise<CertificatesResponse> {
    return this.getClientCertificates();
  }

  /**
   * Search functionality
   */
  async searchSamples(query: string): Promise<SamplesResponse> {
    return this.getClientSamples(1, 20, { search: query });
  }

  async searchCertificates(query: string): Promise<CertificatesResponse> {
    return this.getClientCertificates(1, 20, query);
  }

  /**
   * Utility methods for data formatting
   */
  formatSampleStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'RECEPTION': 'Reception',
      'PREPARATION_MECANIQUE': 'Mechanical Preparation',
      'LABORATOIRE_SAA_PREP_CHIMIQUE': 'SAA Chemical Preparation',
      'LABORATOIRE_SAA_ANALYSE': 'SAA Analysis',
      'LABORATOIRE_ICP_PREP_CHIMIQUE': 'ICP Chemical Preparation',
      'LABORATOIRE_ICP_ANALYSE': 'ICP Analysis',
      'LABORATOIRE_AU_PREP_CHIMIQUE': 'Fire Assay Preparation',
      'LABORATOIRE_AU_ANALYSE': 'Fire Assay Analysis',
      'LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE': 'Volumetric Preparation',
      'LABORATOIRE_VOLUMETRIE_ANALYSE': 'Volumetric Analysis',
      'VALIDATION_SERVICE': 'Quality Control',
      'GENERATION_CERTIFICAT': 'Certificate Generation',
      'PRET': 'Ready for Pickup',
    };

    return statusMap[status] || status;
  }

  formatPriority(priority: string): string {
    const priorityMap: { [key: string]: string } = {
      'URGENTE': 'Urgent',
      'ELEVEE': 'High',
      'NORMALE': 'Normal',
    };

    return priorityMap[priority] || priority;
  }

  formatSampleType(type: string): string {
    const typeMap: { [key: string]: string } = {
      'MINERAI': 'Ore',
      'ROCHE': 'Rock',
      'SOL': 'Soil',
      'SEDIMENT': 'Sediment',
      'CONCENTRE': 'Concentrate',
    };

    return typeMap[type] || type;
  }

  /**
   * Calculate completion percentage based on status
   */
  getCompletionPercentage(status: string): number {
    const statusProgressMap: { [key: string]: number } = {
      'RECEPTION': 10,
      'PREPARATION_MECANIQUE': 20,
      'LABORATOIRE_SAA_PREP_CHIMIQUE': 40,
      'LABORATOIRE_SAA_ANALYSE': 60,
      'LABORATOIRE_ICP_PREP_CHIMIQUE': 40,
      'LABORATOIRE_ICP_ANALYSE': 60,
      'LABORATOIRE_AU_PREP_CHIMIQUE': 40,
      'LABORATOIRE_AU_ANALYSE': 60,
      'LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE': 40,
      'LABORATOIRE_VOLUMETRIE_ANALYSE': 60,
      'VALIDATION_SERVICE': 80,
      'GENERATION_CERTIFICAT': 90,
      'PRET': 100,
    };

    return statusProgressMap[status] || 0;
  }

  /**
   * Get status color theme
   */
  getStatusColor(status: string): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
    switch (status) {
      case 'PRET':
        return 'success';
      case 'RECEPTION':
        return 'warning';
      case 'ANNULE':
        return 'error';
      default:
        return 'primary';
    }
  }

  /**
   * Get priority color theme
   */
  getPriorityColor(priority: string): 'error' | 'warning' | 'default' {
    switch (priority) {
      case 'URGENTE':
        return 'error';
      case 'ELEVEE':
        return 'warning';
      default:
        return 'default';
    }
  }

  /**
   * Date formatting utilities
   */
  formatDate(date: string | Date): string {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  formatDateTime(date: string | Date): string {
    return new Date(date).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatDuration(days: number): string {
    if (days === 0) return 'Today';
    if (days === 1) return '1 day';
    if (days < 7) return `${days} days`;
    if (days < 30) return `${Math.round(days / 7)} weeks`;
    return `${Math.round(days / 30)} months`;
  }

  /**
   * Chart data helpers
   */
  prepareMonthlyTrendsChart(trends: ClientAnalytics['monthly_trends']) {
    return {
      labels: trends.map(trend => trend.month),
      datasets: [
        {
          label: 'Samples Submitted',
          data: trends.map(trend => trend.samples_submitted),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.4,
        },
        {
          label: 'Samples Completed',
          data: trends.map(trend => trend.samples_completed),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.4,
        },
      ],
    };
  }

  prepareStatusDistributionChart(distribution: ClientAnalytics['status_distribution']) {
    return {
      labels: distribution.map(status => this.formatSampleStatus(status.status)),
      datasets: [{
        data: distribution.map(status => status.count),
        backgroundColor: [
          '#FF6384',
          '#36A2EB', 
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40',
        ],
      }],
    };
  }

  prepareLaboratoryDistributionChart(distribution: ClientAnalytics['laboratory_distribution']) {
    return {
      labels: distribution.map(lab => lab.laboratory),
      datasets: [{
        label: 'Sample Count',
        data: distribution.map(lab => lab.count),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      }],
    };
  }

  /**
   * Method aliases for compatibility with ClientDashboard component
   */
  async getSamples(params: any): Promise<SamplesResponse> {
    const { page = 1, limit = 20, ...filters } = params;
    return this.getClientSamples(page, limit, filters);
  }

  async getCertificates(params: any): Promise<CertificatesResponse> {
    const { page = 1, limit = 20, search, dateFrom, dateTo } = params;
    
    // Build query parameters with smart search and date filters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search && search.trim()) {
      queryParams.append('search', search.trim());
    }
    if (dateFrom) {
      queryParams.append('dateFrom', dateFrom);
    }
    if (dateTo) {
      queryParams.append('dateTo', dateTo);
    }

    const result = await apiService.get<CertificatesResponse>(`${this.baseUrl}/certificates?${queryParams.toString()}`);
    
    return result;
  }

  async getNotifications(): Promise<NotificationsResponse> {
    return this.getClientNotifications();
  }
}

export const clientDashboardService = new ClientDashboardService(); 